import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiEye, FiFileText, FiPrinter, FiChevronDown, FiChevronUp
} from 'react-icons/fi';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import {
  collection, query, where, getAggregateFromServer, sum
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getCached, invalidateCache } from '../../utils/fyDataCache';
import { recordReads } from '../../utils/readTelemetry';
import { getAccountsCached, getCustomersCached, getEmployeesCached, getJobsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import { formatAmount } from '../../utils/documentNumbering';
import PageLayout from '../../components/PageLayout';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import SendWhatsAppButton from '../../components/SendWhatsAppButton';
import CustomerSelect from '../../components/CustomerSelect';
import AccountSelect from '../../components/AccountSelect';
import InvoiceAllocationTable from '../../components/InvoiceAllocationTable';
import { createTransaction, updateTransaction, deleteTransaction, updateTransactionStatus } from '../../utils/transactionHelper';
import { getLinkedAccount } from '../../utils/coaInitializer';
import { getPendingInvoices, getInvoicesForEdit, applyInvoiceAllocations, reverseInvoiceAllocations, prefetchInvoices } from '../../utils/invoiceClearingHelper';
import { isFutureDate, isLargeTransaction, LARGE_TRANSACTION_THRESHOLD, checkPartyOverdue } from '../../utils/validationHelpers';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import useDocListPagination from '../../hooks/useDocListPagination';
import { buildReceiptSubTransactionLines, flattenInvoiceAllocations } from '../../utils/multiPartyHelper';
import {
  sumAllocationDeductions, resolveReceiptDeductionAccounts, buildDeductionLines,
  findInvoiceLevelWhtConflicts, round2, receiptGross
} from '../../utils/receiptDeductionsHelper';
import AttachmentPanel from '../../components/AttachmentPanel';
import DocListPagination from '../../components/DocListPagination';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, TotalsSection, TotalsTable, TotalRow, SortableTh, SortContext
} from '../../components/SharedStyles';
import ListDateRange, { inDateRange } from '../../components/ListDateRange';
import { startSaveTimer } from '../../utils/saveTimer';
import { prewarmSave } from '../../utils/transactionHelper';

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Posted: colors.success.main,
};

const TRAN_TYPE_CODE = 'BR';

// Footer summary spec — aggregated server-side in paged mode (~few reads),
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Posted']
};

const BankReceipts = () => {
  const location = useLocation();
  const { activeCompany, activeFinancialYear, currentUser, activeBranch } = useAuth();
  // Speed: prepare the first save in the background as soon as this screen opens.
  useEffect(() => {
    if (!activeCompany?.id || !activeFinancialYear?.id) return;
    prewarmSave(activeCompany.id, {
      tranTypeCode: TRAN_TYPE_CODE,
      branchId: activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.id : null,
      financialYearId: activeFinancialYear.id,
    });
  }, [activeCompany?.id, activeFinancialYear?.id, activeBranch?.id]);
  const { canEdit, canDelete, canChange } = usePagePermissions('transactions', 'bankReceipt');
  const showBranchCol = activeBranch && activeBranch.id === 'all';
  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => { if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [activeCompany]);

  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  // List date filter (From / To). Empty = all dates, as before.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All' || dateFrom !== '' || dateTo !== ''
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: records,
    loading,
    refresh: loadRecords,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [saving, setSaving] = useState(false);

  // Mode: 'party', 'petty', or 'multi-party'
  const [mode, setMode] = useState('party');

  // Party mode state
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [invoiceAllocations, setInvoiceAllocations] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // Petty mode state
  // Petty mode state — multiple contra accounts on a single voucher.
  const EMPTY_CONTRA_LINE = { accountId: '', accountCode: '', accountName: '', amount: 0, description: '' };
  const [contraLines, setContraLines] = useState([{ ...EMPTY_CONTRA_LINE }]);
  const contraLinesTotal = useMemo(
    () => contraLines.reduce((s, l) => s + (Number(l.amount) || 0), 0),
    [contraLines]
  );

  // Multi-party mode state
  const [partyLines, setPartyLines] = useState([]);
  const [expandedPartyLines, setExpandedPartyLines] = useState({});

  // Per-invoice WHT / write-off totals (party mode) for the amount breakdown.
  const partyDeductions = useMemo(() => sumAllocationDeductions(invoiceAllocations), [invoiceAllocations]);

  const [formData, setFormData] = useState({
    docDate: new Date().toISOString().split('T')[0],
    customerId: '', customerName: '', bankAccountId: '', bankAccountName: '',
    paymentMode: 'Cheque', chequeNo: '', chequeDate: '', draweeBank: '',
    employeeId: '', employeeName: '', jobId: '', jobName: '',
    totalAmount: 0, reference: '', narration: '', notes: ''
  });
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  // Balances are filled ONE ACCOUNT AT A TIME, on demand (see requestAccountBalance).
  // Starts empty: an id missing from this map means "not asked for yet".
  const [accountBalances, setAccountBalances] = useState({});
  const [balanceLoading, setBalanceLoading] = useState({});

  // Searchable dropdown state
  const [accountSearch, setAccountSearch] = useState('');
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowAccountDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-calculate totalAmount from party lines in multi-party mode
  useEffect(() => {
    if (mode === 'multi-party') {
      const total = partyLines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
      setFormData(prev => ({ ...prev, totalAmount: total }));
    }
  }, [mode, partyLines]);

  // Record list now comes from useDocListPagination above (loadRecords = its
  // refresh). The old full-history getTransactions load lived here.

  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getCustomersCached(activeCompany.id);
      setCustomers(rows.filter(c => c.isActive === true).sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '')));
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, [activeCompany]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading jobs:', error); }
  }, [activeCompany]);

  // Load Level 5 Bank accounts (under 11102 - Bank Accounts)
  const loadBankAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const allAccounts = await getAccountsCached(activeCompany.id);

      // Filter for Level 5 accounts under Bank (code starts with 11102)
      const bankAccs = allAccounts.filter(a =>
        a.level === 5 &&
        a.code?.startsWith('11102') &&
        a.isActive !== false
      ).sort((a, b) => (a.code || '').localeCompare(b.code || ''));

      setBankAccounts(bankAccs);
      // Firebase cost: balances are NOT loaded here any more — see requestAccountBalance.
    } catch (error) {
      console.error('Error loading bank accounts:', error);
    }
  }, [activeCompany]);

  // Balance for ONE bank account, fetched only when the user asks for it.
  //
  // Firebase cost: the old loader ran on every mount of this screen (and again
  // after every save) and downloaded the company's ENTIRE posted ledger for the
  // financial year — ~83,000 documents on a large company — just to draw the
  // small "Bal:" label in the picker. This does the same arithmetic
  // (Σ debit − Σ credit over Posted rows of the active year for that account)
  // with a server-side aggregation: 1 read, one account, on demand.
  //
  // Cached per company + financial year + account for the session, so tapping
  // the same account twice is free. Cleared after this device saves a voucher.
  const requestAccountBalance = useCallback(async (accountId) => {
    if (!activeCompany?.id || !activeFinancialYear?.id || !accountId) return;
    const companyId = activeCompany.id;
    const fyId = activeFinancialYear.id;
    setBalanceLoading(prev => ({ ...prev, [accountId]: true }));
    try {
      const balance = await getCached(
        `acctBal|${companyId}|${fyId}|${accountId}`,
        async () => {
          const snap = await getAggregateFromServer(
            query(
              collection(db, 'companies', companyId, 'subTransactions'),
              where('financialYearId', '==', fyId),
              where('status', '==', 'Posted'),
              where('accountId', '==', accountId)
            ),
            { dr: sum('debitAmount'), cr: sum('creditAmount') }
          );
          recordReads(companyId, 1);
          const d = snap.data();
          return (d.dr || 0) - (d.cr || 0);
        }
      );
      setAccountBalances(prev => ({ ...prev, [accountId]: balance }));
    } catch (error) {
      console.error('Error loading account balance:', error);
    } finally {
      setBalanceLoading(prev => { const next = { ...prev }; delete next[accountId]; return next; });
    }
  }, [activeCompany, activeFinancialYear]);

  // NOTE: the record list itself loads inside useDocListPagination — not here.
  useEffect(() => {
    loadCustomers();
    loadBankAccounts();
    loadEmployees();
    loadJobs();
  }, [loadCustomers, loadBankAccounts, loadEmployees, loadJobs]);

  // Filter accounts based on search
  const filteredBankAccounts = useMemo(() => {
    if (!accountSearch.trim()) return bankAccounts;
    const search = accountSearch.toLowerCase();
    return bankAccounts.filter(acc =>
      (acc.code || '').toLowerCase().includes(search) ||
      (acc.name || '').toLowerCase().includes(search)
    );
  }, [bankAccounts, accountSearch]);

  const filteredRecords = useMemo(() => {
    let filtered = records;
    if (statusFilter !== 'All') filtered = filtered.filter(r => r.status === statusFilter);
    if (dateFrom || dateTo) filtered = filtered.filter(r => inDateRange(r.docDate, dateFrom, dateTo));
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.docNumber?.toLowerCase().includes(q) ||
        r.partyName?.toLowerCase().includes(q) ||
        r.contraAccountName?.toLowerCase().includes(q) ||
        r.notes?.toLowerCase().includes(q) ||
        r.reference?.toLowerCase().includes(q) ||
        r.chequeNo?.toLowerCase().includes(q) ||
        (r.partyLines || []).some(pl => pl.partyName?.toLowerCase().includes(q)) ||
        // Find the voucher that settled an invoice: search its invoice no.
        (r.invoiceAllocations || []).some(a => a.docNumber?.toLowerCase().includes(q)) ||
        (r.partyLines || []).some(pl => (pl.invoiceAllocations || []).some(a => a.docNumber?.toLowerCase().includes(q)))
      );
    }
    return sort.sortItems(filtered);
  }, [records, statusFilter, searchQuery, sort, dateFrom, dateTo]);

  const handleHeaderChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomerChange = async (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setFormData(prev => ({
      ...prev,
      customerId: customerId,
      customerName: customer ? customer.companyName : ''
    }));

    // Load pending invoices for party mode
    if (mode === 'party' && customerId && activeCompany?.id) {
      setLoadingInvoices(true);
      try {
        const invoices = await getPendingInvoices(activeCompany.id, 'customer', customerId);
        setPendingInvoices(invoices);
      } catch (error) {
        console.error('Error loading pending invoices:', error);
        setPendingInvoices([]);
      } finally {
        setLoadingInvoices(false);
      }
    } else {
      setPendingInvoices([]);
    }
    setInvoiceAllocations([]);
  };

  const handleAccountSelect = (account) => {
    setFormData(prev => ({
      ...prev,
      bankAccountId: account.id,
      bankAccountName: `${account.code} - ${account.name}`
    }));
    setAccountSearch(`${account.code} - ${account.name}`);
    setShowAccountDropdown(false);
  };

  // === Petty contra-line management (multi-row) ===
  const addContraLine = () => {
    setContraLines(prev => [...prev, { ...EMPTY_CONTRA_LINE }]);
  };
  const removeContraLine = (idx) => {
    setContraLines(prev => prev.length <= 1 ? [{ ...EMPTY_CONTRA_LINE }] : prev.filter((_, i) => i !== idx));
  };
  const updateContraLine = (idx, field, value) => {
    setContraLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };
  const setContraLineAccount = (idx, account) => {
    setContraLines(prev => prev.map((l, i) => i === idx ? {
      ...l,
      accountId: account.id,
      accountCode: account.code,
      accountName: account.name
    } : l));
  };

  // === Multi-party line management ===
  const addPartyLine = () => {
    setPartyLines(prev => [...prev, {
      partyType: 'customer',
      partyId: '',
      partyName: '',
      partyAccountId: '',
      partyAccountCode: '',
      amount: 0,
      invoiceAllocations: [],
      _pendingInvoices: [],
      _loadingInvoices: false
    }]);
  };

  const removePartyLine = (index) => {
    setPartyLines(prev => prev.filter((_, i) => i !== index));
    setExpandedPartyLines(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handlePartyLineCustomerChange = async (index, customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setPartyLines(prev => prev.map((line, i) => i === index ? {
      ...line,
      partyId: customerId,
      partyName: customer?.companyName || '',
      partyAccountId: customer?.linkedAccountId || '',
      partyAccountCode: customer?.linkedAccountCode || '',
      invoiceAllocations: [],
      _pendingInvoices: [],
      _loadingInvoices: true
    } : line));

    if (customerId && activeCompany?.id) {
      try {
        const invoices = await getPendingInvoices(activeCompany.id, 'customer', customerId);
        setPartyLines(prev => prev.map((line, i) => i === index ? {
          ...line,
          _pendingInvoices: invoices,
          _loadingInvoices: false
        } : line));
      } catch (error) {
        setPartyLines(prev => prev.map((line, i) => i === index ? {
          ...line, _pendingInvoices: [], _loadingInvoices: false
        } : line));
      }
    }
  };

  const handlePartyLineChange = (index, field, value) => {
    setPartyLines(prev => prev.map((line, i) => i === index ? { ...line, [field]: value } : line));
  };

  const handlePartyLineAllocationChange = (index, allocations) => {
    setPartyLines(prev => prev.map((line, i) => i === index ? { ...line, invoiceAllocations: allocations } : line));
  };

  const togglePartyLineInvoices = (index) => {
    setExpandedPartyLines(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleModeSwitch = (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    // Reset mode-specific fields
    setPendingInvoices([]);
    setInvoiceAllocations([]);
    setContraLines([{ ...EMPTY_CONTRA_LINE }]);
    setPartyLines([]);
    setExpandedPartyLines({});
    setFormData(prev => ({
      ...prev,
      customerId: '', customerName: '', totalAmount: 0
    }));
  };

  const resetForm = () => {
    setFormData({
      docDate: new Date().toISOString().split('T')[0],
      customerId: '', customerName: '', bankAccountId: '', bankAccountName: '',
      paymentMode: 'Cheque', chequeNo: '', chequeDate: '', draweeBank: '',
      employeeId: '', employeeName: '', jobId: '', jobName: '',
      totalAmount: 0, reference: '', narration: '', notes: ''
    });
    setAccountSearch('');
    setEditingRecord(null);
    setPendingInvoices([]);
    setInvoiceAllocations([]);
    setContraLines([{ ...EMPTY_CONTRA_LINE }]);
    setPartyLines([]);
    setExpandedPartyLines({});
    setMode('party');
  };

  const handleCreate = () => { resetForm(); setViewMode('form'); };

  const handleEdit = async (rec) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditingRecord(rec);
    const recMode = rec.receiptMode || 'party';
    setMode(recMode);

    const bankAcc = bankAccounts.find(a => a.id === rec.bankAccountId);
    setFormData({
      docDate: rec.docDate || new Date().toISOString().split('T')[0],
      customerId: rec.partyId || '',
      customerName: rec.partyName || '',
      bankAccountId: rec.bankAccountId || '',
      bankAccountName: bankAcc ? `${bankAcc.code} - ${bankAcc.name}` : '',
      paymentMode: rec.paymentMode || 'Cheque',
      chequeNo: rec.chequeNo || '',
      chequeDate: rec.chequeDate || '',
      draweeBank: rec.draweeBank || '',
      employeeId: rec.employeeId || '',
      employeeName: rec.employeeName || '',
      jobId: rec.jobId || '',
      jobName: rec.jobName || '',
      // The form's Total Amount is the GROSS cleared (saved totalAmount = cash).
      totalAmount: receiptGross(rec),
      reference: rec.reference || '',
      narration: rec.notes || '',
      notes: rec.internalNotes || ''
    });
    setAccountSearch(bankAcc ? `${bankAcc.code} - ${bankAcc.name}` : '');

    if (recMode === 'petty') {
      if (Array.isArray(rec.contraLines) && rec.contraLines.length > 0) {
        setContraLines(rec.contraLines.map(l => ({
          accountId: l.accountId || '',
          accountCode: l.accountCode || '',
          accountName: l.accountName || '',
          amount: Number(l.amount) || 0,
          description: l.description || ''
        })));
      } else if (rec.contraAccountId) {
        setContraLines([{
          accountId: rec.contraAccountId,
          accountCode: rec.contraAccountCode || '',
          accountName: rec.contraAccountName || '',
          amount: Number(rec.totalAmount) || 0,
          description: rec.notes || ''
        }]);
      } else {
        setContraLines([{ ...EMPTY_CONTRA_LINE }]);
      }
    } else if (recMode === 'multi-party') {
      // Restore party lines with pending invoices loaded for each
      const lines = rec.partyLines || [];
      const restoredLines = await Promise.all(lines.map(async (pl) => {
        let pendingInvs = [];
        const lineAllocs = pl.invoiceAllocations || [];
        if (pl.partyId && activeCompany?.id) {
          try {
            pendingInvs = await getInvoicesForEdit(activeCompany.id, 'customer', pl.partyId, lineAllocs);
          } catch (e) { /* ignore */ }
        }
        return {
          ...pl,
          _pendingInvoices: pendingInvs,
          _loadingInvoices: false
        };
      }));
      setPartyLines(restoredLines);
    } else {
      // Load pending invoices with previously allocated ones restored for editing
      const existingAllocs = rec.invoiceAllocations || [];
      if (rec.partyId && activeCompany?.id) {
        try {
          const invoices = await getInvoicesForEdit(activeCompany.id, 'customer', rec.partyId, existingAllocs);
          setPendingInvoices(invoices);
        } catch (error) {
          setPendingInvoices([]);
        }
      }
      setInvoiceAllocations(existingAllocs);
    }

    setViewMode('form');
  };

  const handleView = (rec) => { setViewingRecord(rec); setViewMode('view'); };

  // Handle navigation from reports (e.g., Journal Report clicking on document #).
  // With pagination the target may not be in the loaded page — ensureDoc
  // fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(rec => {
      if (rec) {
        handleView(rec);
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, ensureDoc]);

  // Resolve a customer's ledger account id/code robustly. Prefers the pointer
  // stored on the customer record; if that's blank (Walk-in Customer, or any
  // legacy record) it finds the account by the customer link, which is how the
  // account got its balance in the first place. Throws a plain-language error if
  // there is genuinely no account, so the save fails cleanly instead of writing
  // an undefined accountId that Firestore rejects with a cryptic message.
  const resolveCustomerAccount = async (customer, customerId, customerName) => {
    if (customer?.linkedAccountId) {
      return { id: customer.linkedAccountId, code: customer.linkedAccountCode || '' };
    }
    const linked = await getLinkedAccount(activeCompany.id, 'customer', customerId);
    if (linked?.id) return { id: linked.id, code: linked.code || '' };
    throw new Error(
      `"${customerName || 'This customer'}" has no ledger account yet. Open ` +
      `Master Data → Chart of Accounts → click "Repair COA", then try again.`
    );
  };

  const handleSave = async (saveStatus) => {
    const _screenTimer = startSaveTimer('Whole click: Bank Receipt'); // save timer — see console
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (mode === 'party') {
      if (!formData.customerId) { alert('Please select a Customer'); return; }
    } else if (mode === 'petty') {
      const validContras = contraLines.filter(l => l.accountId && Number(l.amount) > 0);
      if (validContras.length === 0) { alert('Please add at least one contra account with an amount'); return; }
    } else if (mode === 'multi-party') {
      const validLines = partyLines.filter(l => l.partyId && Number(l.amount) > 0);
      if (validLines.length < 2) { alert('Multi-party mode requires at least 2 party lines with amounts'); return; }
      for (let i = 0; i < partyLines.length; i++) {
        const pl = partyLines[i];
        if (pl.partyId && pl.invoiceAllocations.length > 0) {
          const allocTotal = pl.invoiceAllocations.reduce((s, a) => s + (a.allocatedAmount || 0), 0);
          if (allocTotal > Number(pl.amount) + 0.01) {
            alert(`Line ${i + 1} (${pl.partyName}): allocated amount exceeds line amount`); return;
          }
        }
      }
    }
    if (!formData.bankAccountId) { alert('Please select a Bank Account'); return; }
    if (!formData.narration.trim()) { alert('Please enter Narration'); return; }
    const checkAmount = mode === 'petty' ? contraLinesTotal : Number(formData.totalAmount);
    if (checkAmount <= 0) { alert('Total Amount must be greater than 0'); return; }
    if (!activeFinancialYear?.id) { alert('No active financial year selected'); return; }

    // Validate allocations don't exceed receipt amount (party mode)
    if (mode === 'party' && invoiceAllocations.length > 0) {
      const totalAllocated = invoiceAllocations.reduce((sum, a) => sum + (a.allocatedAmount || 0), 0);
      if (totalAllocated > Number(formData.totalAmount) + 0.01) {
        alert('Total allocated amount cannot exceed receipt amount'); return;
      }
    }

    // WHT entered here on an invoice whose Sale Invoice ALREADY deducted WHT
    // ("deduct" direction) would count WHT Receivable twice — confirm first.
    {
      const conflicts = mode === 'party'
        ? findInvoiceLevelWhtConflicts(invoiceAllocations, pendingInvoices)
        : mode === 'multi-party'
          ? partyLines.flatMap(pl => findInvoiceLevelWhtConflicts(pl.invoiceAllocations, pl._pendingInvoices))
          : [];
      if (conflicts.length > 0 && !window.confirm(
        `WHT was already deducted on the Sale Invoice for ${conflicts.join(', ')}.\n\n` +
        `Entering WHT again on this receipt will count it twice. Continue anyway?`
      )) return;
    }

    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return;
    }

    if (saveStatus === 'Posted' && checkAmount <= 0) {
      alert('Cannot post a zero-amount transaction'); return;
    }

    if (mode === 'party' && formData.customerId) {
      const overdue = await checkPartyOverdue(activeCompany.id, formData.customerId, 'customer');
      if (overdue.overdueCount > 0) {
        if (!window.confirm(`This customer has ${overdue.overdueCount} overdue invoice(s) totaling ${activeCompany?.currencySymbol || 'Rs.'} ${overdue.overdueAmount.toLocaleString()}. Continue?`)) return;
      }
    }

    if (isLargeTransaction(checkAmount)) {
      if (!window.confirm('This is a large transaction. Please verify the amount. Continue?')) return;
    }

    setSaving(true);
    try {
      const status = saveStatus || 'Draft';
      const selectedBankAccount = bankAccounts.find(a => a.id === formData.bankAccountId);

      let transactionData;

      if (mode === 'petty') {
        // Petty mode: Dr Bank (total), Cr each Contra Account
        const cleanContraLines = contraLines.filter(l => l.accountId && Number(l.amount) > 0)
          .map(l => ({
            accountId: l.accountId,
            accountCode: l.accountCode || '',
            accountName: l.accountName || '',
            amount: Number(l.amount),
            description: l.description || ''
          }));
        const totalPetty = cleanContraLines.reduce((s, l) => s + l.amount, 0);
        const firstContra = cleanContraLines[0];
        transactionData = {
          tranTypeCode: 'BR',
          docDate: formData.docDate,
          financialYearId: activeFinancialYear.id,
          branchId: activeBranch?.id || null,
          branchName: activeBranch?.branchName || null,
          partyType: null,
          partyId: null,
          partyName: null,
          receiptMode: 'petty',
          contraLines: cleanContraLines,
          contraAccountId: firstContra.accountId,
          contraAccountCode: firstContra.accountCode,
          contraAccountName: cleanContraLines.length > 1
            ? `${firstContra.accountName} +${cleanContraLines.length - 1} more`
            : firstContra.accountName,
          bankAccountId: formData.bankAccountId,
          bankAccountCode: selectedBankAccount?.code || '',
          bankAccountName: selectedBankAccount?.name || formData.bankAccountName,
          employeeId: formData.employeeId,
          employeeName: formData.employeeName,
          jobId: formData.jobId,
          jobName: formData.jobName,
          paymentMode: formData.paymentMode,
          chequeNo: formData.paymentMode === 'Cheque' ? formData.chequeNo : '',
          chequeDate: formData.paymentMode === 'Cheque' ? formData.chequeDate : '',
          draweeBank: formData.draweeBank,
          totalAmount: totalPetty,
          reference: formData.reference,
          notes: formData.narration,
          internalNotes: formData.notes,
          status,
          subTransactionLines: [
            {
              accountId: formData.bankAccountId,
              accountCode: selectedBankAccount?.code || '',
              accountName: selectedBankAccount?.name || formData.bankAccountName,
              debitAmount: totalPetty,
              creditAmount: 0,
              description: `Bank receipt - ${formData.narration}`
            },
            ...cleanContraLines.map(l => ({
              accountId: l.accountId,
              accountCode: l.accountCode,
              accountName: l.accountName,
              debitAmount: 0,
              creditAmount: l.amount,
              description: l.description || formData.narration
            }))
          ]
        };
      } else if (mode === 'multi-party') {
        // Multi-party mode: Dr Bank (total), Cr each Customer (their amount)
        const cleanPartyLines = partyLines.filter(l => l.partyId && Number(l.amount) > 0).map(l => {
          // Per-invoice WHT / write-off on this customer's line. `amount` stays the
          // GROSS cleared for the customer; netAmount = cash actually received.
          const ded = sumAllocationDeductions(l.invoiceAllocations);
          return {
            partyType: 'customer',
            partyId: l.partyId,
            partyName: l.partyName,
            partyAccountId: l.partyAccountId,
            partyAccountCode: l.partyAccountCode,
            amount: Number(l.amount),
            whtAmount: ded.wht,
            adjustmentAmount: ded.adj,
            netAmount: round2(Number(l.amount) - ded.wht - ded.adj),
            invoiceAllocations: l.invoiceAllocations || []
          };
        });
        const mpWht = round2(cleanPartyLines.reduce((s, l) => s + l.whtAmount, 0));
        const mpAdj = round2(cleanPartyLines.reduce((s, l) => s + l.adjustmentAmount, 0));
        const mpGross = round2(cleanPartyLines.reduce((s, l) => s + l.amount, 0));
        const mpNet = round2(mpGross - mpWht - mpAdj);
        const mpDeductionAccounts = (mpWht > 0 || mpAdj > 0)
          ? await resolveReceiptDeductionAccounts(activeCompany.id, { needWht: mpWht > 0, needBadDebt: mpAdj > 0 })
          : null;

        // Backfill any missing ledger account (e.g. Walk-in) before the legs are
        // built, so we never emit a credit line with an undefined accountId.
        for (const l of cleanPartyLines) {
          if (!l.partyAccountId) {
            const acct = await resolveCustomerAccount(
              customers.find(c => c.id === l.partyId), l.partyId, l.partyName
            );
            l.partyAccountId = acct.id;
            l.partyAccountCode = acct.code;
          }
        }

        const flatAllocations = flattenInvoiceAllocations(cleanPartyLines);
        const subLines = buildReceiptSubTransactionLines(
          { id: formData.bankAccountId, code: selectedBankAccount?.code || '', name: selectedBankAccount?.name || '' },
          cleanPartyLines,
          formData.narration,
          mpDeductionAccounts
        );

        transactionData = {
          tranTypeCode: 'BR',
          docDate: formData.docDate,
          financialYearId: activeFinancialYear.id,
          branchId: activeBranch?.id || null,
          branchName: activeBranch?.branchName || null,
          partyType: null,
          partyId: null,
          partyName: null,
          receiptMode: 'multi-party',
          partyLines: cleanPartyLines,
          invoiceAllocations: flatAllocations,
          bankAccountId: formData.bankAccountId,
          bankAccountCode: selectedBankAccount?.code || '',
          bankAccountName: selectedBankAccount?.name || formData.bankAccountName,
          employeeId: formData.employeeId,
          employeeName: formData.employeeName,
          jobId: formData.jobId,
          jobName: formData.jobName,
          paymentMode: formData.paymentMode,
          chequeNo: formData.paymentMode === 'Cheque' ? formData.chequeNo : '',
          chequeDate: formData.paymentMode === 'Cheque' ? formData.chequeDate : '',
          draweeBank: formData.draweeBank,
          // totalAmount = CASH actually received (what receipts have always meant —
          // dashboard Recovery / Collection reports read it). WHT / bad-debt are
          // stored alongside; gross = cash + WHT + bad debt (see receiptGross —
          // `grossAmount` is a reserved alias the save code overwrites).
          totalAmount: mpNet,
          netAmount: mpNet,
          whtAmount: mpWht,
          adjustmentAmount: mpAdj,
          reference: formData.reference,
          notes: formData.narration,
          internalNotes: formData.notes,
          status,
          subTransactionLines: subLines
        };
      } else {
        // Party mode: Dr Bank, Cr Customer
        const customer = customers.find(c => c.id === formData.customerId);
        const customerName = customer?.companyName || formData.customerName || '';
        // See resolveCustomerAccount — Walk-in and legacy records may have no
        // stored linkedAccountId; resolve it so we never write an undefined leg.
        const custAccount = await resolveCustomerAccount(customer, formData.customerId, customerName);
        const invoiceNarration = invoiceAllocations.length > 0
          ? `Against ${invoiceAllocations.map(a => a.docNumber).join(', ')}`
          : formData.narration;
        // Per-invoice WHT / write-off: the typed Total Amount is the GROSS cleared
        // from the customer; the bank only receives gross − WHT − write-off.
        const gross = round2(Number(formData.totalAmount));
        const ded = sumAllocationDeductions(invoiceAllocations);
        const netCash = round2(gross - ded.wht - ded.adj);
        const deductionAccounts = (ded.wht > 0 || ded.adj > 0)
          ? await resolveReceiptDeductionAccounts(activeCompany.id, { needWht: ded.wht > 0, needBadDebt: ded.adj > 0 })
          : null;
        const partySubLines = [];
        if (!deductionAccounts || netCash > 0) {
          partySubLines.push({
            accountId: formData.bankAccountId,
            accountCode: selectedBankAccount?.code || '',
            accountName: selectedBankAccount?.name || formData.bankAccountName,
            debitAmount: deductionAccounts ? netCash : Number(formData.totalAmount),
            creditAmount: 0,
            description: `Bank receipt from ${customerName} - ${invoiceNarration}`
          });
        }
        if (deductionAccounts) {
          partySubLines.push(...buildDeductionLines(invoiceAllocations, deductionAccounts, customerName));
        }
        partySubLines.push({
          accountId: custAccount.id,
          accountCode: custAccount.code,
          accountName: `Customer: ${customerName}`,
          debitAmount: 0,
          creditAmount: Number(formData.totalAmount),
          description: `Receipt from ${customerName} - ${invoiceNarration}`
        });
        transactionData = {
          tranTypeCode: 'BR',
          docDate: formData.docDate,
          financialYearId: activeFinancialYear.id,
          branchId: activeBranch?.id || null,
          branchName: activeBranch?.branchName || null,
          partyType: 'customer',
          partyId: formData.customerId,
          partyName: customerName,
          receiptMode: 'party',
          bankAccountId: formData.bankAccountId,
          bankAccountCode: selectedBankAccount?.code || '',
          bankAccountName: selectedBankAccount?.name || formData.bankAccountName,
          employeeId: formData.employeeId,
          employeeName: formData.employeeName,
          jobId: formData.jobId,
          jobName: formData.jobName,
          paymentMode: formData.paymentMode,
          chequeNo: formData.paymentMode === 'Cheque' ? formData.chequeNo : '',
          chequeDate: formData.paymentMode === 'Cheque' ? formData.chequeDate : '',
          draweeBank: formData.draweeBank,
          // totalAmount = CASH actually received (unchanged when no deductions).
          totalAmount: netCash,
          netAmount: netCash,
          whtAmount: ded.wht,
          adjustmentAmount: ded.adj,
          reference: formData.reference,
          notes: formData.narration,
          internalNotes: formData.notes,
          status,
          subTransactionLines: partySubLines
        };
      }

      let transactionId;
      let newTranData = null;

      if (editingRecord) {
        // Reverse old allocations before applying new ones
        if (editingRecord.invoiceAllocations && editingRecord.invoiceAllocations.length > 0) {
          await reverseInvoiceAllocations(activeCompany.id, editingRecord.id);
        }

        await updateTransaction(activeCompany.id, editingRecord.id, transactionData, currentUser.uid);
        if (status !== editingRecord.status) {
          await updateTransactionStatus(activeCompany.id, editingRecord.id, status, currentUser.uid);
        }
        transactionId = editingRecord.id;
      } else {
        // Speed: start reading the invoices to settle while the document saves.
        if (status === 'Posted' && (mode === 'party' || mode === 'multi-party')) {
          const pre = mode === 'multi-party' ? transactionData.invoiceAllocations : invoiceAllocations;
          prefetchInvoices(activeCompany.id, (pre || []).filter(a => a && a.allocatedAmount > 0).map(a => a.invoiceId));
        }
        newTranData = await createTransaction(activeCompany.id, transactionData, currentUser.uid);
        // Speed: only needed if the document was not already created as Posted.
        if (status === 'Posted' && newTranData.status !== 'Posted') {
          await updateTransactionStatus(activeCompany.id, newTranData.id, 'Posted', currentUser.uid);
        }
        transactionId = newTranData.id;
      }

      // Apply invoice allocations when posted
      const allocs = mode === 'multi-party' ? transactionData.invoiceAllocations : invoiceAllocations;
      if ((mode === 'party' || mode === 'multi-party') && allocs.length > 0 && status === 'Posted') {
        await applyInvoiceAllocations(activeCompany.id, allocs, transactionId);
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedRecord = editingRecord
        ? { ...editingRecord, ...transactionData, id: transactionId, status }
        : { ...newTranData, status };
      applyLocal(prev => editingRecord
        ? prev.map(r => r.id === transactionId ? savedRecord : r)
        : [savedRecord, ...prev]);

      setViewMode('list');
      resetForm();

      // This voucher just changed a bank account's balance — drop the cached
      // per-account balances so the picker recalculates (only if asked again).
      invalidateCache(`acctBal|${activeCompany.id}|`);
      setAccountBalances({});

      // Background refresh — reconciles with server data without blocking UI.
      Promise.all([loadRecords(), loadBankAccounts()]).catch(err =>
        console.error('Background refresh failed:', err)
      );
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving: ' + error.message);
    } finally {
      _screenTimer.end();
      setSaving(false);
    }
  };

  const handleDelete = async (rec) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete ${rec.docNumber}?`)) return;
    try {
      // Reverse invoice allocations if any
      if (rec.invoiceAllocations && rec.invoiceAllocations.length > 0) {
        await reverseInvoiceAllocations(activeCompany.id, rec.id);
      }
      await deleteTransaction(activeCompany.id, rec.id);
      await loadRecords();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Error deleting: ' + error.message);
    }
  };

  const getAccountDisplay = (accountId) => {
    const account = bankAccounts.find(a => a.id === accountId);
    return account ? `${account.code} - ${account.name}` : '-';
  };

  const getRecordPartyDisplay = (rec) => {
    const recMode = rec.receiptMode || 'party';
    if (recMode === 'petty') {
      return rec.contraAccountName ? `${rec.contraAccountCode || ''} - ${rec.contraAccountName}` : '-';
    }
    if (recMode === 'multi-party') {
      const count = (rec.partyLines || []).length;
      return `${count} ${count === 1 ? 'party' : 'parties'}`;
    }
    return rec.partyName || '-';
  };

  const getModeBadgeProps = (rec) => {
    const recMode = rec.receiptMode || 'party';
    if (recMode === 'petty') return { $petty: true, children: 'Petty' };
    if (recMode === 'multi-party') return { $multi: true, children: 'Multi' };
    return { children: 'Party' };
  };

  const handlePrint = (rec) => {
    const src = rec || viewingRecord;
    if (!src) return;
    const recMode = src.receiptMode || 'party';
    const partyName = recMode === 'petty'
      ? `${src.contraAccountCode ? src.contraAccountCode + ' - ' : ''}${src.contraAccountName || 'Petty'}`
      : recMode === 'multi-party'
        ? `Multi-Party (${(src.partyLines || []).length} customers)`
        : (src.partyName || '-');
    const data = {
      ...src,
      partyName,
      bankAccountName: getAccountDisplay(src.bankAccountId),
      referenceNo: src.chequeNo || src.reference || src.referenceNo || '',
      narration: src.narration || src.notes || '',
    };
    printDocument('bankReceipt', data, activeCompany, printConfig, activeBranch);
  };

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox><FiSearch />
          <input type="text" placeholder="Search... (also by invoice no)" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <ListDateRange from={dateFrom} to={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Posted">Posted</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Bank Receipt</TranAddButton>}
        </FilterGroup>
      </ActionBar>
      {loading ? <LoadingMessage>Loading...</LoadingMessage> : filteredRecords.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Bank Receipt Records</h3>
          <p>{records.length === 0 ? 'Create your first bank receipt voucher' : 'No records match filters'}</p>
          {records.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Record</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer><SortContext.Provider value={sort.ctxValue}><Table><thead><tr>
          <SortableTh field="docNumber">Voucher No</SortableTh><SortableTh field="docDate">Date</SortableTh>{showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}<Th>Mode</Th><Th>Customer / Account</Th><Th>Bank Account</Th><SortableTh field="paymentMode">Pay Mode</SortableTh>
          <Th style={{ textAlign: 'right' }}>Amount</Th><Th style={{ textAlign: 'right' }}>WHT</Th><Th style={{ textAlign: 'right' }}>Bad Debt</Th>
          <SortableTh field="totalAmount" style={{ textAlign: 'right' }}>Net Received</SortableTh><SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
          <Th style={{ textAlign: 'center' }}>Actions</Th>
        </tr></thead><tbody>
          {filteredRecords.map(rec => {
            const badgeProps = getModeBadgeProps(rec);
            return (
            <Tr key={rec.id}>
              <Td><strong>{rec.docNumber}</strong></Td><Td>{rec.docDate}</Td>
              {showBranchCol && <Td>{rec.branchName || '-'}</Td>}
              <Td><ModeBadge $petty={badgeProps.$petty} $multi={badgeProps.$multi}>{badgeProps.children}</ModeBadge></Td>
              <Td>{getRecordPartyDisplay(rec)}</Td><Td>{getAccountDisplay(rec.bankAccountId)}</Td>
              <Td>{rec.paymentMode || '-'}</Td>
              {/* Amount = gross cleared; Net Received = cash (saved totalAmount). */}
              <Td style={{ textAlign: 'right' }}>{formatAmount(receiptGross(rec))}</Td>
              <Td style={{ textAlign: 'right' }}>{Number(rec.whtAmount) > 0 ? formatAmount(rec.whtAmount) : '-'}</Td>
              <Td style={{ textAlign: 'right' }}>{Number(rec.adjustmentAmount) > 0 ? formatAmount(rec.adjustmentAmount) : '-'}</Td>
              <Td style={{ textAlign: 'right', fontWeight: 600 }}>{activeCompany?.currencyCode || 'PKR'} {formatAmount(rec.totalAmount)}</Td>
              <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[rec.status]}>{rec.status}</StatusBadge></Td>
              <Td style={{ textAlign: 'center' }}><ActionGroup>
                <IconBtn title="View" onClick={() => handleView(rec)}><FiEye /></IconBtn>
                {canChange && <IconBtn title="Edit" onClick={() => handleEdit(rec)}><FiEdit2 /></IconBtn>}
                <IconBtn title="Print" onClick={() => handlePrint(rec)}><FiPrinter /></IconBtn>
                {rec.status === 'Posted' && (
                  <SendWhatsAppButton templateKey="bankReceipt" doc={rec} party={customers.find(c => c.id === rec.partyId)} variant="icon" />
                )}
                {canDelete && <IconBtn title="Delete" $danger onClick={() => handleDelete(rec)}><FiTrash2 /></IconBtn>}
              </ActionGroup></Td>
            </Tr>
            );
          })}
        </tbody></Table></SortContext.Provider></TableContainer>
      )}
      <DocListPagination {...pagination} />

      <SummaryBar>
        <span>Total: <strong>{listFiltering ? filteredRecords.length : pagination.totalCount}</strong> record(s)</span>
        <span>Draft: <strong>{listSummary.counts.Draft || 0}</strong> | Posted: <strong>{listSummary.counts.Posted || 0}</strong></span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingRecord) return null;
    const rec = viewingRecord;
    const recMode = rec.receiptMode || 'party';
    const recHasDeductions = Number(rec.whtAmount) > 0 || Number(rec.adjustmentAmount) > 0;
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingRecord(null); }}><FiArrowLeft /> Back to List</BackButton>
          <FormTitle>{rec.docNumber}
            <ModeBadge $petty={recMode === 'petty'} $multi={recMode === 'multi-party'} style={{ marginLeft: 8 }}>{recMode === 'petty' ? 'Petty' : recMode === 'multi-party' ? 'Multi' : 'Party'}</ModeBadge>
            <StatusBadge $color={STATUS_COLORS[rec.status]} style={{ marginLeft: 8 }}>{rec.status}</StatusBadge>
          </FormTitle>
          <FormActions>
            {canChange && <SaveButton onClick={() => handleEdit(rec)}><FiEdit2 /> Edit</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(rec)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>
        <FormCard>
          <SectionTitle>Receipt Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>Voucher No</label><span>{rec.docNumber}</span></InfoItem>
            <InfoItem><label>Date</label><span>{rec.docDate}</span></InfoItem>
            {recMode === 'petty' ? (
              <InfoItem $span2><label>Contra Account</label><span>{rec.contraAccountCode} - {rec.contraAccountName || '-'}</span></InfoItem>
            ) : recMode === 'multi-party' ? (
              <InfoItem $span2><label>Mode</label><span>Multi-Party ({(rec.partyLines || []).length} customers)</span></InfoItem>
            ) : (
              <InfoItem><label>Customer</label><span>{rec.partyName || '-'}</span></InfoItem>
            )}
            <InfoItem><label>Bank Account</label><span>{getAccountDisplay(rec.bankAccountId)}</span></InfoItem>
            <InfoItem><label>Payment Mode</label><span>{rec.paymentMode || '-'}</span></InfoItem>
            <InfoItem><label>Drawee Bank</label><span>{rec.draweeBank || '-'}</span></InfoItem>
            {rec.paymentMode === 'Cheque' && <InfoItem><label>Cheque No</label><span>{rec.chequeNo || '-'}</span></InfoItem>}
            {rec.paymentMode === 'Cheque' && <InfoItem><label>Cheque Date</label><span>{rec.chequeDate || '-'}</span></InfoItem>}
            <InfoItem><label>Reference</label><span>{rec.reference || '-'}</span></InfoItem>
            <InfoItem $span2><label>Narration</label><span>{rec.notes || '-'}</span></InfoItem>
            <InfoItem><label>Employee</label><span>{rec.employeeName || '-'}</span></InfoItem>
            <InfoItem><label>Job</label><span>{rec.jobName || '-'}</span></InfoItem>
          </InfoGrid>
        </FormCard>

        {/* Petty contra lines in view mode */}
        {recMode === 'petty' && Array.isArray(rec.contraLines) && rec.contraLines.length > 0 && (
          <FormCard>
            <SectionTitle>Contra Accounts</SectionTitle>
            <TableContainer><Table><thead><tr>
              <Th>#</Th><Th>Account</Th><Th>Description</Th>
              <Th style={{ textAlign: 'right' }}>Amount ({activeCompany?.currencyCode || 'PKR'})</Th>
            </tr></thead><tbody>
              {rec.contraLines.map((cl, i) => (
                <Tr key={i}>
                  <Td>{i + 1}</Td>
                  <Td>{cl.accountCode ? `${cl.accountCode} - ` : ''}{cl.accountName || '-'}</Td>
                  <Td>{cl.description || '-'}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(cl.amount)}</Td>
                </Tr>
              ))}
              <Tr>
                <Td colSpan={3} style={{ fontWeight: 700 }}>Total</Td>
                <Td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(rec.totalAmount)}</Td>
              </Tr>
            </tbody></Table></TableContainer>
          </FormCard>
        )}

        {/* Multi-party lines in view mode */}
        {recMode === 'multi-party' && rec.partyLines && rec.partyLines.length > 0 && (
          <FormCard>
            <SectionTitle>Party Lines</SectionTitle>
            <TableContainer><Table><thead><tr>
              <Th>#</Th><Th>Customer</Th><Th style={{ textAlign: 'right' }}>Amount ({activeCompany?.currencyCode || 'PKR'})</Th>
              {recHasDeductions && <><Th style={{ textAlign: 'right' }}>WHT</Th><Th style={{ textAlign: 'right' }}>Bad Debt / Adj.</Th><Th style={{ textAlign: 'right' }}>Net Received</Th></>}
              <Th>Invoices Allocated</Th>
            </tr></thead><tbody>
              {rec.partyLines.map((pl, i) => (
                <Tr key={i}>
                  <Td>{i + 1}</Td><Td>{pl.partyName || '-'}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(pl.amount)}</Td>
                  {recHasDeductions && (
                    <>
                      <Td style={{ textAlign: 'right' }}>{Number(pl.whtAmount) > 0 ? formatAmount(pl.whtAmount) : '-'}</Td>
                      <Td style={{ textAlign: 'right' }}>{Number(pl.adjustmentAmount) > 0 ? formatAmount(pl.adjustmentAmount) : '-'}</Td>
                      <Td style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(pl.netAmount ?? pl.amount)}</Td>
                    </>
                  )}
                  <Td>{(pl.invoiceAllocations || []).length > 0 ? pl.invoiceAllocations.map(a => a.docNumber).join(', ') : '-'}</Td>
                </Tr>
              ))}
              <Tr>
                <Td colSpan={2} style={{ fontWeight: 700 }}>Total</Td>
                <Td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(receiptGross(rec))}</Td>
                {recHasDeductions && (
                  <>
                    <Td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(rec.whtAmount || 0)}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(rec.adjustmentAmount || 0)}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(rec.totalAmount)}</Td>
                  </>
                )}
                <Td></Td>
              </Tr>
            </tbody></Table></TableContainer>
          </FormCard>
        )}

        <FormCard>
          <SectionTitle>Amount</SectionTitle>
          <TotalsSection><TotalsTable><tbody>
            {recHasDeductions ? (
              <>
                <SubTotalRow><td>Total Amount — gross ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(receiptGross(rec))}</td></SubTotalRow>
                {Number(rec.whtAmount) > 0 && <SubTotalRow><td>Less: WHT deducted by customer</td><td>({formatAmount(rec.whtAmount)})</td></SubTotalRow>}
                {Number(rec.adjustmentAmount) > 0 && <SubTotalRow><td>Less: Adjustment / Bad Debt</td><td>({formatAmount(rec.adjustmentAmount)})</td></SubTotalRow>}
                <TotalRow><td>Net Received — cash ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(rec.totalAmount)}</td></TotalRow>
              </>
            ) : (
              <TotalRow><td>Total Amount ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(rec.totalAmount)}</td></TotalRow>
            )}
          </tbody></TotalsTable></TotalsSection>
        </FormCard>
        {(recMode === 'party') && rec.invoiceAllocations && rec.invoiceAllocations.length > 0 && (
          <FormCard>
            <SectionTitle>Invoice Allocations</SectionTitle>
            <TableContainer><Table><thead><tr>
              <Th>Invoice No</Th><Th style={{ textAlign: 'right' }}>Allocated Amount</Th>
              {recHasDeductions && <><Th style={{ textAlign: 'right' }}>WHT %</Th><Th style={{ textAlign: 'right' }}>WHT</Th><Th style={{ textAlign: 'right' }}>Bad Debt / Adj.</Th><Th style={{ textAlign: 'right' }}>Net</Th></>}
            </tr></thead><tbody>
              {rec.invoiceAllocations.map((a, i) => (
                <Tr key={i}>
                  <Td>{a.docNumber}</Td>
                  <Td style={{ textAlign: 'right' }}>{activeCompany?.currencyCode || 'PKR'} {formatAmount(a.allocatedAmount)}</Td>
                  {recHasDeductions && (
                    <>
                      <Td style={{ textAlign: 'right' }}>{Number(a.whtRate) > 0 ? `${a.whtRate}%` : '-'}</Td>
                      <Td style={{ textAlign: 'right' }}>{Number(a.whtAmount) > 0 ? formatAmount(a.whtAmount) : '-'}</Td>
                      <Td style={{ textAlign: 'right' }}>{Number(a.adjustmentAmount) > 0 ? formatAmount(a.adjustmentAmount) : '-'}</Td>
                      <Td style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(round2((Number(a.allocatedAmount) || 0) - (Number(a.whtAmount) || 0) - (Number(a.adjustmentAmount) || 0)))}</Td>
                    </>
                  )}
                </Tr>
              ))}
            </tbody></Table></TableContainer>
          </FormCard>
        )}
        {rec.internalNotes && <FormCard><SectionTitle>Internal Notes</SectionTitle><NotesText>{rec.internalNotes}</NotesText></FormCard>}
        <AttachmentPanel transactionId={rec?.id} attachment={rec?.attachment} canEdit={false} />
      </>
    );
  };

  const renderMultiPartyLines = () => (
    <FormCard>
      <SectionTitle>Party Lines</SectionTitle>
      <LineTableContainer>
        <LineTable>
          <thead>
            <tr>
              <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh>
              <LineTh style={{ width: 260, minWidth: 260, maxWidth: 260 }}>Customer</LineTh>
              <LineTh style={{ width: 150, textAlign: 'right' }}>Amount — gross ({activeCompany?.currencyCode || 'PKR'})</LineTh>
              <LineTh style={{ width: 100, textAlign: 'right' }}>WHT</LineTh>
              <LineTh style={{ width: 110, textAlign: 'right' }}>Bad Debt / Adj.</LineTh>
              <LineTh style={{ width: 120, textAlign: 'right' }}>Net Received</LineTh>
              <LineTh style={{ width: 100, textAlign: 'center' }}>Invoices</LineTh>
              <LineTh style={{ width: 50, textAlign: 'center' }}></LineTh>
            </tr>
          </thead>
          <tbody>
            {partyLines.map((line, idx) => {
              // WHT / write-off come from the per-invoice columns of this line.
              const lineDed = sumAllocationDeductions(line.invoiceAllocations);
              const lineNet = round2((Number(line.amount) || 0) - lineDed.wht - lineDed.adj);
              return (
              <React.Fragment key={idx}>
                <tr>
                  <LineTd>{idx + 1}</LineTd>
                  <LineTd>
                    <CustomerSelect
                      customers={customers}
                      selectedCustomerId={line.partyId}
                      onChange={(custId) => handlePartyLineCustomerChange(idx, custId)}
                    />
                  </LineTd>
                  <LineTd>
                    <LineInput
                      type="number" min="0" step="0.01"
                      value={line.amount}
                      onChange={e => handlePartyLineChange(idx, 'amount', e.target.value)}
                      style={{ textAlign: 'right' }}
                    />
                  </LineTd>
                  <LineTd style={{ textAlign: 'right', color: colors.text.secondary }}>{lineDed.wht > 0 ? formatAmount(lineDed.wht) : '-'}</LineTd>
                  <LineTd style={{ textAlign: 'right', color: colors.text.secondary }}>{lineDed.adj > 0 ? formatAmount(lineDed.adj) : '-'}</LineTd>
                  <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(lineNet)}</LineTd>
                  <LineTd style={{ textAlign: 'center' }}>
                    {line.partyId && (
                      <ExpandBtn onClick={() => togglePartyLineInvoices(idx)}>
                        {expandedPartyLines[idx] ? <FiChevronUp /> : <FiChevronDown />}
                        {(line.invoiceAllocations || []).length > 0 && ` (${line.invoiceAllocations.length})`}
                      </ExpandBtn>
                    )}
                  </LineTd>
                  <LineTd style={{ textAlign: 'center' }}>
                    <RemoveLineBtn onClick={() => removePartyLine(idx)} title="Remove line">&times;</RemoveLineBtn>
                  </LineTd>
                </tr>
                {expandedPartyLines[idx] && line.partyId && (
                  <tr>
                    <td colSpan={8} style={{ padding: '8px 12px', background: colors.neutral[50] }}>
                      {line._loadingInvoices ? (
                        <LoadingMessage>Loading invoices...</LoadingMessage>
                      ) : (
                        <InvoiceAllocationTable
                          invoices={line._pendingInvoices || []}
                          totalReceiptAmount={Number(line.amount)}
                          allocations={line.invoiceAllocations || []}
                          onAllocationsChange={(allocs) => handlePartyLineAllocationChange(idx, allocs)}
                          showDeductions
                        />
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
          </tbody>
        </LineTable>
      </LineTableContainer>
      <AddLineButton onClick={addPartyLine}><FiPlus /> Add Customer Line</AddLineButton>
      {(() => {
        const mpGross = round2(partyLines.reduce((s, l) => s + (Number(l.amount) || 0), 0));
        const mpDed = sumAllocationDeductions(partyLines.flatMap(l => l.invoiceAllocations || []));
        const cur = activeCompany?.currencyCode || 'PKR';
        return (
          <TotalsSection style={{ marginTop: spacing.md }}>
            <TotalsTable><tbody>
              {(mpDed.wht > 0 || mpDed.adj > 0) ? (
                <>
                  <SubTotalRow><td>Total Amount — gross ({cur})</td><td>{formatAmount(mpGross)}</td></SubTotalRow>
                  {mpDed.wht > 0 && <SubTotalRow><td>Less: WHT deducted by customers</td><td>({formatAmount(mpDed.wht)})</td></SubTotalRow>}
                  {mpDed.adj > 0 && <SubTotalRow><td>Less: Adjustment / Bad Debt</td><td>({formatAmount(mpDed.adj)})</td></SubTotalRow>}
                  <TotalRow><td>Net Received — cash ({cur})</td><td>{formatAmount(round2(mpGross - mpDed.wht - mpDed.adj))}</td></TotalRow>
                </>
              ) : (
                <TotalRow><td>Total Amount ({cur})</td><td>{formatAmount(mpGross)}</td></TotalRow>
              )}
            </tbody></TotalsTable>
          </TotalsSection>
        );
      })()}
    </FormCard>
  );

  const renderFormView = () => (
    <>
      <FormHeader>
        <BackButton onClick={() => { setViewMode('list'); resetForm(); }}><FiArrowLeft /> Back to List</BackButton>
        <FormTitle>{editingRecord ? `Edit: ${editingRecord.docNumber}` : 'New Bank Receipt'}</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}><FiX /> Cancel</SaveButton>
          {canEdit && !(editingRecord && editingRecord.status && editingRecord.status !== 'Draft' && editingRecord.status !== 'Cancelled') && <SaveButton onClick={() => handleSave('Draft')} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Save Draft'}</SaveButton>}
          {canEdit && (!editingRecord || editingRecord.status === 'Draft') && (
            <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}><FiCheck /> Save & Post</SaveButton>
          )}
          {/* ACC-05: editing a posted document keeps it posted (no silent drop to Draft) */}
          {canEdit && (editingRecord && editingRecord.status && editingRecord.status !== 'Draft' && editingRecord.status !== 'Cancelled') && (
            <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}><FiCheck /> {saving ? 'Saving...' : 'Save Changes'}</SaveButton>
          )}
        </FormActions>
      </FormHeader>

      {/* Mode Toggle */}
      <ModeToggleBar>
        <ModeButton $active={mode === 'party'} onClick={() => handleModeSwitch('party')}>Party Receipt</ModeButton>
        <ModeButton $active={mode === 'petty'} onClick={() => handleModeSwitch('petty')}>Petty Receipt</ModeButton>
        <ModeButton $active={mode === 'multi-party'} onClick={() => handleModeSwitch('multi-party')}>Multi-Party</ModeButton>
      </ModeToggleBar>

      <FormCard>
        <SectionTitle>{mode === 'petty' ? 'Petty Receipt Details' : mode === 'multi-party' ? 'Multi-Party Receipt Details' : 'Receipt Details'}</SectionTitle>
        <FormGrid>
          <FormGroup><Label>Voucher No</Label><Input value={editingRecord?.docNumber || 'Auto-generated'} disabled /></FormGroup>
          <FormGroup><Label>Date *</Label><Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} /></FormGroup>

          {mode === 'petty' ? (
            <FormGroup $span2>
              <Label>Contra Accounts</Label>
              <Input value={`${contraLines.filter(l => l.accountId).length} line(s) — see table below`} disabled />
            </FormGroup>
          ) : mode === 'party' ? (
            <FormGroup $span2><Label>Customer *</Label>
              <CustomerSelect customers={customers} selectedCustomerId={formData.customerId} onChange={handleCustomerChange} />
            </FormGroup>
          ) : null}

          <FormGroup $span2>
            <Label>Bank Account * (with Balance)</Label>
            <SearchableDropdown ref={dropdownRef}>
              <SearchInput
                type="text"
                placeholder="Search bank accounts..."
                value={accountSearch}
                onChange={(e) => {
                  setAccountSearch(e.target.value);
                  setShowAccountDropdown(true);
                  if (!e.target.value) {
                    setFormData(prev => ({ ...prev, bankAccountId: '', bankAccountName: '' }));
                  }
                }}
                onFocus={() => setShowAccountDropdown(true)}
              />
              {formData.bankAccountId && (
                <ClearBtn onClick={() => {
                  setFormData(prev => ({ ...prev, bankAccountId: '', bankAccountName: '' }));
                  setAccountSearch('');
                }}>x</ClearBtn>
              )}
              {showAccountDropdown && (
                <DropdownList>
                  {filteredBankAccounts.length === 0 ? (
                    <DropdownItem disabled>No bank accounts found</DropdownItem>
                  ) : (
                    filteredBankAccounts.map(account => (
                      <DropdownItem
                        key={account.id}
                        onClick={() => handleAccountSelect(account)}
                        $selected={formData.bankAccountId === account.id}
                      >
                        <AccountCode>{account.code}</AccountCode>
                        <AccountName>{account.name}</AccountName>
                        {/* Firebase cost: the balance is fetched per account, on
                            request — the chip replaces a full-ledger download. */}
                        {accountBalances[account.id] !== undefined ? (
                          <AccountBalanceLabel $negative={accountBalances[account.id] < 0}>
                            Bal: {formatAmount(accountBalances[account.id] || 0)}
                          </AccountBalanceLabel>
                        ) : balanceLoading[account.id] ? (
                          <BalanceLink as="span" $busy>…</BalanceLink>
                        ) : (
                          <BalanceLink
                            title="Show this account's balance"
                            onClick={(e) => { e.stopPropagation(); requestAccountBalance(account.id); }}
                          >Show balance</BalanceLink>
                        )}
                      </DropdownItem>
                    ))
                  )}
                </DropdownList>
              )}
            </SearchableDropdown>
          </FormGroup>
          <FormGroup><Label>Payment Mode *</Label>
            <Select value={formData.paymentMode} onChange={e => handleHeaderChange('paymentMode', e.target.value)}>
              <option value="Cheque">Cheque</option>
              <option value="RTGS">RTGS</option>
              <option value="Online">Online Transfer</option>
              <option value="Wire">Wire Transfer</option>
            </Select>
          </FormGroup>
          {formData.paymentMode === 'Cheque' && (
            <>
              <FormGroup><Label>Cheque No</Label><Input value={formData.chequeNo} onChange={e => handleHeaderChange('chequeNo', e.target.value)} placeholder="Cheque number" /></FormGroup>
              <FormGroup><Label>Cheque Date</Label><Input type="date" value={formData.chequeDate} onChange={e => handleHeaderChange('chequeDate', e.target.value)} /></FormGroup>
            </>
          )}
          <FormGroup><Label>Drawee Bank</Label><Input value={formData.draweeBank} onChange={e => handleHeaderChange('draweeBank', e.target.value)} placeholder="Customer's bank name" /></FormGroup>
          <FormGroup><Label>Reference</Label><Input value={formData.reference} onChange={e => handleHeaderChange('reference', e.target.value)} placeholder="Reference number" /></FormGroup>
          <FormGroup $span2><Label>Narration *</Label><Input value={formData.narration} onChange={e => handleHeaderChange('narration', e.target.value)} placeholder="Receipt description (required)" /></FormGroup>
          <FormGroup>
            <Label>Employee</Label>
            <Select value={formData.employeeId} onChange={e => {
              const emp = employees.find(em => em.id === e.target.value);
              handleHeaderChange('employeeId', e.target.value);
              handleHeaderChange('employeeName', emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '');
            }}>
              <option value="">-- Select Employee --</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Job</Label>
            <Select value={formData.jobId} onChange={e => {
              const job = jobs.find(j => j.id === e.target.value);
              handleHeaderChange('jobId', e.target.value);
              handleHeaderChange('jobName', job?.jobName || '');
            }}>
              <option value="">-- Select Job --</option>
              {jobs.map(job => (
                <option key={job.id} value={job.id}>{job.jobCode ? job.jobCode + ' - ' : ''}{job.jobName}</option>
              ))}
            </Select>
          </FormGroup>
        </FormGrid>
      </FormCard>

      {/* Multi-party lines */}
      {mode === 'multi-party' && renderMultiPartyLines()}

      {/* Petty mode: multiple contra accounts table */}
      {mode === 'petty' && (
        <FormCard>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <SectionTitle style={{ margin: 0 }}>Contra Accounts</SectionTitle>
            <AddLineButton onClick={addContraLine}><FiPlus /> Add Contra Line</AddLineButton>
          </div>
          <LineTableContainer>
            <LineTable>
              <thead><tr>
                <LineTh style={{ width: 32, minWidth: 32 }}>#</LineTh>
                <LineTh style={{ minWidth: 280 }}>Account *</LineTh>
                <LineTh style={{ minWidth: 200 }}>Description</LineTh>
                <LineTh style={{ width: 130, textAlign: 'right' }}>Amount *</LineTh>
                <LineTh style={{ width: 32 }}></LineTh>
              </tr></thead>
              <tbody>
                {contraLines.map((line, idx) => (
                  <tr key={idx}>
                    <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{idx + 1}</LineTd>
                    <LineTd>
                      <AccountSelect
                        companyId={activeCompany?.id}
                        value={line.accountId}
                        onChange={(account) => setContraLineAccount(idx, account)}
                        label="Contra Account"
                      />
                    </LineTd>
                    <LineTd>
                      <LineInput value={line.description} onChange={e => updateContraLine(idx, 'description', e.target.value)} placeholder="Optional note" />
                    </LineTd>
                    <LineTd>
                      <LineInput type="number" min="0" step="0.01" value={line.amount}
                        onChange={e => updateContraLine(idx, 'amount', Number(e.target.value))}
                        style={{ textAlign: 'right', fontWeight: 600 }} />
                    </LineTd>
                    <LineTd style={{ textAlign: 'center' }}>
                      <RemoveLineBtn onClick={() => removeContraLine(idx)}><FiX /></RemoveLineBtn>
                    </LineTd>
                  </tr>
                ))}
              </tbody>
            </LineTable>
          </LineTableContainer>
          <TotalsSection><TotalsTable><tbody>
            <TotalRow>
              <td>Total Petty Receipt ({activeCompany?.currencyCode || 'PKR'})</td>
              <td>{formatAmount(contraLinesTotal)}</td>
            </TotalRow>
          </tbody></TotalsTable></TotalsSection>
        </FormCard>
      )}

      {/* Invoice Allocation Table - Party mode only */}
      {mode === 'party' && formData.customerId && (
        <FormCard>
          <SectionTitle>Invoice Allocation</SectionTitle>
          {loadingInvoices ? (
            <LoadingMessage>Loading pending invoices...</LoadingMessage>
          ) : (
            <InvoiceAllocationTable
              invoices={pendingInvoices}
              totalReceiptAmount={Number(formData.totalAmount)}
              allocations={invoiceAllocations}
              onAllocationsChange={setInvoiceAllocations}
              showDeductions
            />
          )}
        </FormCard>
      )}

      {/* Amount Details - only show manual input for party mode */}
      {mode === 'party' && (
        <FormCard>
          <SectionTitle>Amount Details</SectionTitle>
          <FormGrid>
            <FormGroup $span2><Label>Total Amount — gross cleared, incl. WHT &amp; bad debt ({activeCompany?.currencyCode || 'PKR'}) *</Label>
              <Input type="number" min="0" step="0.01" value={formData.totalAmount} onChange={e => handleHeaderChange('totalAmount', e.target.value)} style={{ textAlign: 'right', fontSize: '16px', fontWeight: 600 }} />
            </FormGroup>
          </FormGrid>
          <TotalsSection><TotalsTable><tbody>
            {(partyDeductions.wht > 0 || partyDeductions.adj > 0) ? (
              <>
                <SubTotalRow><td>Total Amount — gross ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(Number(formData.totalAmount))}</td></SubTotalRow>
                {partyDeductions.wht > 0 && <SubTotalRow><td>Less: WHT deducted by customer</td><td>({formatAmount(partyDeductions.wht)})</td></SubTotalRow>}
                {partyDeductions.adj > 0 && <SubTotalRow><td>Less: Adjustment / Bad Debt</td><td>({formatAmount(partyDeductions.adj)})</td></SubTotalRow>}
                <TotalRow><td>Net Received — cash ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(round2(Number(formData.totalAmount) - partyDeductions.wht - partyDeductions.adj))}</td></TotalRow>
              </>
            ) : (
              <TotalRow><td>Total Amount ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(Number(formData.totalAmount))}</td></TotalRow>
            )}
          </tbody></TotalsTable></TotalsSection>
        </FormCard>
      )}

      <FormCard>
        <SectionTitle>Internal Notes</SectionTitle>
        <FormGrid><FormGroup $span2><TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Internal notes (not printed)..." rows={3} /></FormGroup></FormGrid>
      </FormCard>

      <AttachmentPanel
        transactionId={editingRecord?.id}
        attachment={editingRecord?.attachment}
        canEdit={canEdit}
        onChange={({ attachment }) => setEditingRecord(prev => prev ? { ...prev, attachment } : prev)}
      />
    </>
  );

  useDocumentShortcuts({
    onNew: () => { if (!canEdit) { alert('You do not have permission to create.'); return; } handleCreate(); },
    onSave: () => { if (!canEdit) { alert('You do not have permission to save changes.'); return; } handleSave('Posted'); },
    viewMode,
    saving
  });

  return (
    <PageLayout pageTitle="Bank Receipt" breadcrumb="Receipts / Bank Receipt">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

// ============ UNIQUE STYLED COMPONENTS ============
const ModeBadge = styled.span`display:inline-block;padding:2px 8px;border-radius:8px;font-size:${typography.fontSize.xs};font-weight:${typography.fontWeight.medium};color:${props => props.$petty ? colors.warning.dark || '#7c5e00' : props.$multi ? '#6b21a8' : colors.primary.main};background:${props => props.$petty ? colors.warning.main + '20' : props.$multi ? '#a855f720' : colors.primary.main + '15'};`;

// Mode toggle styles
const ModeToggleBar = styled.div`display:flex;gap:0;margin-top:${spacing.md};border:1px solid ${colors.border.light};border-radius:${borderRadius.md};overflow:hidden;width:fit-content;`;
const ModeButton = styled.button`padding:${spacing.sm} ${spacing.lg};font-size:${typography.fontSize.sm};font-weight:${typography.fontWeight.medium};border:none;cursor:pointer;transition:all 0.2s;background:${props => props.$active ? colors.primary.main : colors.background.default};color:${props => props.$active ? 'white' : colors.text.secondary};&:hover{background:${props => props.$active ? colors.primary.main : colors.neutral[100]};}`;

// Searchable dropdown styles
const SearchableDropdown = styled.div`position:relative;width:100%;`;
const SearchInput = styled.input`width:100%;padding:${spacing.sm} 35px ${spacing.sm} ${spacing.md};border:1px solid ${colors.border.light};border-radius:${borderRadius.sm};font-size:${typography.fontSize.sm};color:${colors.text.primary};outline:none;&:focus{border-color:${colors.primary.main};}`;
const ClearBtn = styled.button`position:absolute;right:8px;top:50%;transform:translateY(-50%);background:${colors.neutral[200]};border:none;border-radius:50%;width:20px;height:20px;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:14px;color:${colors.text.secondary};&:hover{background:${colors.neutral[300]};}`;
const DropdownList = styled.div`position:absolute;top:100%;left:0;right:0;max-height:250px;overflow-y:auto;background:white;border:1px solid ${colors.border.light};border-radius:${borderRadius.sm};box-shadow:${shadows.md};z-index:100;margin-top:4px;`;
const DropdownItem = styled.div`padding:10px 12px;display:flex;align-items:center;gap:12px;cursor:${props => props.disabled ? 'default' : 'pointer'};background:${props => props.$selected ? colors.primary.main + '10' : 'transparent'};color:${props => props.disabled ? colors.text.tertiary : colors.text.primary};font-size:13px;&:hover{background:${props => props.disabled ? 'transparent' : colors.neutral[100]};}&:not(:last-child){border-bottom:1px solid ${colors.neutral[100]};}`;
const AccountCode = styled.span`font-family:monospace;font-size:12px;color:${colors.primary.main};background:${colors.primary.main}10;padding:2px 6px;border-radius:4px;`;
const AccountName = styled.span`flex:1;`;
const AccountBalanceLabel = styled.span`font-size:12px;font-weight:600;color:${props => props.$negative ? colors.error.main : colors.success.main};`;
const BalanceLink = styled.button`font-size:11px;font-weight:600;white-space:nowrap;padding:1px 6px;border-radius:3px;border:1px dashed ${colors.text.tertiary}55;background:transparent;color:${colors.text.tertiary};cursor:pointer;line-height:1.6;opacity:${props => props.$busy ? 0.6 : 1};&:hover{color:${colors.primary.main};border-color:${colors.primary.main}66;background:${colors.primary.main}0d;}`;

const SubTotalRow = styled.tr`td{font-size:${typography.fontSize.sm};}`;

// Multi-party line table styles
const LineTableContainer = styled.div`overflow:visible;margin-top:${spacing.sm};`;
const LineTable = styled.table`width:100%;border-collapse:collapse;`;
const LineTh = styled.th`padding:8px 12px;text-align:left;font-size:${typography.fontSize.xs};font-weight:${typography.fontWeight.semibold};color:${colors.text.secondary};background:${colors.neutral[50]};border-bottom:2px solid ${colors.border.light};`;
const LineTd = styled.td`padding:6px 12px;border-bottom:1px solid ${colors.border.light};vertical-align:middle;`;
const LineInput = styled.input`width:100%;padding:6px 8px;border:1px solid ${colors.border.light};border-radius:${borderRadius.sm};font-size:${typography.fontSize.sm};outline:none;&:focus{border-color:${colors.primary.main};}`;
const RemoveLineBtn = styled.button`background:none;border:none;color:${colors.error.main};font-size:18px;cursor:pointer;padding:2px 6px;border-radius:4px;&:hover{background:${colors.error.main}10;}`;
const AddLineButton = styled.button`display:flex;align-items:center;gap:6px;margin-top:${spacing.sm};padding:${spacing.sm} ${spacing.md};background:${colors.primary.main}10;color:${colors.primary.main};border:1px dashed ${colors.primary.main};border-radius:${borderRadius.sm};font-size:${typography.fontSize.sm};cursor:pointer;&:hover{background:${colors.primary.main}20;}`;
const ExpandBtn = styled.button`display:inline-flex;align-items:center;gap:4px;background:none;border:1px solid ${colors.border.light};border-radius:${borderRadius.sm};padding:2px 8px;font-size:${typography.fontSize.xs};color:${colors.text.secondary};cursor:pointer;&:hover{background:${colors.neutral[50]};}`;

export default BankReceipts;
